import React from 'react';
import { Helmet } from 'react-helmet';
import Box from '@mui/material/Box';
import Main from '../../layouts/Main';
import { Hero, Services, CommunityHighlight, WhyChooseUs, RelatedLocations, ServiceLinks } from '../Location/components';
import { useTheme } from "@mui/material/styles";
import { Typography, Container, Grid, Button, Paper } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

/**
 * Dedicated page for Clayton lawn care and landscaping services
 * with rich content specific to this location for improved SEO.
 */
const ClaytonLocation = () => {
  const theme = useTheme();
  const locationName = "clayton";

  return (
    <Main colorInvert={true}>
      <Helmet>
        <title>Professional Lawn Care & Landscaping Services in Clayton, NC | Wright Brothers Lawn Care</title>
        <meta
          name="description"
          content="Expert lawn care and landscaping in Clayton, NC. Family-owned company providing lawn maintenance, landscape design, turf care, and sod installation with free quotes and 100% satisfaction guarantee."
        />
        <meta
          name="keywords"
          content="lawn care Clayton NC, landscaping Clayton NC, sod installation Johnston County, commercial landscaping Clayton, turfcare services Clayton, lawn maintenance Clayton, landscape design Johnston County, lawn fertilization Clayton, weed control Clayton, professional lawn service Johnston County, commercial lawn care Clayton, residential landscaping Clayton, Wright Brothers Lawn Care"
        />
        <link rel="canonical" href="https://wrightbrotherslawncare.com/clayton" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://wrightbrotherslawncare.com/clayton" />
        <meta property="og:title" content="Professional Lawn Care & Landscaping Services in Clayton, NC | Wright Brothers Lawn Care" />
        <meta property="og:description" content="Expert lawn care and landscaping in Clayton, NC. Family-owned company providing lawn maintenance, landscape design, turf care, and sod installation with free quotes and 100% satisfaction guarantee." />
        <meta property="og:image" content="https://wrightbrotherslawncare.com/images/clayton-landscaping.jpg" />
        <meta property="og:site_name" content="Wright Brothers Lawn Care" />
        <meta property="og:locale" content="en_US" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Professional Lawn Care & Landscaping Services in Clayton, NC | Wright Brothers Lawn Care" />
        <meta name="twitter:description" content="Expert lawn care and landscaping in Clayton, NC. Family-owned company providing lawn maintenance, landscape design, turf care, and sod installation." />
        <meta name="twitter:image" content="https://wrightbrotherslawncare.com/images/clayton-landscaping.jpg" />

        {/* Additional Meta Tags */}
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="geo.region" content="US-NC" />
        <meta name="geo.placename" content="Clayton" />
        <meta name="geo.position" content="35.6507;-78.4564" />
        <meta name="ICBM" content="35.6507, -78.4564" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LandscapingBusiness",
            "name": "Wright Brothers Lawn Care - Clayton",
            "image": "https://wrightbrotherslawncare.com/images/clayton-landscaping.jpg",
            "description": "Professional lawn care and landscaping services in Clayton, NC. We offer lawn maintenance, landscape design, turfcare, sod installation, and commercial landscaping services.",
            "priceRange": "$$",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "2562 US-70",
              "addressLocality": "Goldsboro",
              "addressRegion": "NC",
              "postalCode": "27530",
              "addressCountry": "US"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": "35.6507",
              "longitude": "-78.4564"
            },
            "url": "https://wrightbrotherslawncare.com/clayton",
            "telephone": "+1-919-252-9130",
            "openingHoursSpecification": [
              {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                "opens": "08:00",
                "closes": "18:00"
              }
            ],
            "areaServed": [
              {
                "@type": "City",
                "name": "Clayton",
                "containedInPlace": {
                  "@type": "State",
                  "name": "North Carolina"
                }
              },
              {
                "@type": "City",
                "name": "Garner",
                "containedInPlace": {
                  "@type": "State",
                  "name": "North Carolina"
                }
              },
              {
                "@type": "City",
                "name": "Smithfield",
                "containedInPlace": {
                  "@type": "State",
                  "name": "North Carolina"
                }
              },
              {
                "@type": "City",
                "name": "Knightdale",
                "containedInPlace": {
                  "@type": "State",
                  "name": "North Carolina"
                }
              }
            ],
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Lawn Care Services in Clayton",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Lawn Maintenance in Clayton",
                    "description": "Regular mowing, edging, and lawn care to keep your Clayton property looking its best."
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Landscape Design in Clayton",
                    "description": "Custom landscape designs that enhance your Clayton property's beauty and value."
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Sod Installation in Clayton",
                    "description": "Expert sod installation for a lush, green lawn in Clayton that lasts for years."
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Commercial Landscaping in Clayton",
                    "description": "Professional commercial landscaping services for businesses in Clayton."
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Turfcare in Clayton",
                    "description": "Specialized turfcare services including fertilization and weed control for Clayton lawns."
                  }
                }
              ]
            },
            "sameAs": [
              "https://www.facebook.com/wrightbrotherslawncare",
              "https://www.instagram.com/wrightbrotherslawncare",
              "https://twitter.com/wblawncare"
            ],
            "potentialAction": {
              "@type": "ReserveAction",
              "target": {
                "@type": "EntryPoint",
                "urlTemplate": "https://wrightbrotherslawncare.com/contact",
                "inLanguage": "en-US",
                "actionPlatform": [
                  "http://schema.org/DesktopWebPlatform",
                  "http://schema.org/IOSPlatform",
                  "http://schema.org/AndroidPlatform"
                ]
              },
              "result": {
                "@type": "Reservation",
                "name": "Request a Free Quote"
              }
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "reviewCount": "127",
              "bestRating": "5"
            }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {"@type": "ListItem", "position": 1, "name": "Home", "item": "https://wrightbrotherslawncare.com/"},
              {"@type": "ListItem", "position": 2, "name": "Clayton", "item": "https://wrightbrotherslawncare.com/clayton"}
            ]
          })}
        </script>
      </Helmet>

      <Hero location={locationName} />

      <Box sx={{ py: 4, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            align="center"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '2.5rem' },
              mb: 4
            }}
          >
            Lawn Care & Landscaping in Clayton, NC
          </Typography>
          <Typography
            variant="body1"
            paragraph
            sx={{
              fontSize: '1.1rem',
              lineHeight: 1.7,
              mb: 2
            }}
          >
            Clayton's unique position as a rapidly growing suburb of Raleigh creates distinctive landscaping needs. As one of the fastest-growing communities in Johnston County, Clayton features a mix of established neighborhoods and new developments, each requiring tailored lawn care approaches to maintain curb appeal and property value.
          </Typography>
          <Typography
            variant="body1"
            paragraph
            sx={{
              fontSize: '1.1rem',
              lineHeight: 1.7,
              mb: 2
            }}
          >
            The area's transition from agricultural heritage to suburban community is reflected in its varied soil conditions. Many properties feature the clay-heavy soil common throughout the Triangle region, requiring specific soil amendment strategies and drainage solutions to create healthy growing environments for lawns and ornamental plants.
          </Typography>
          <Typography
            variant="body1"
            paragraph
            sx={{
              fontSize: '1.1rem',
              lineHeight: 1.7,
              mb: 2
            }}
          >
            Clayton's warm, humid climate supports traditional Southern landscape elements while also providing opportunities for innovative sustainable practices. We specialize in creating landscapes that balance aesthetic appeal with environmental responsibility, incorporating native plants that thrive in Clayton's specific conditions. Our designs account for the area's seasonal weather patterns, including hot summers and occasional drought conditions, ensuring beautiful, resilient outdoor spaces throughout the year.
          </Typography>
        </Container>
      </Box>

      <Services location={locationName} />
      <Box bgcolor={theme.palette.primary.main}>
        <CommunityHighlight location={locationName} />
      </Box>
      <WhyChooseUs location={locationName} />
      
      <ServiceLinks location={locationName} />
      
      <RelatedLocations location={locationName} />

      <Box sx={{ py: 6, bgcolor: theme.palette.background.default }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            component="h2"
            gutterBottom
            align="center"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.75rem', md: '2.25rem' },
              mb: 5
            }}
          >
            Contact Our Clayton Team
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Paper elevation={3} sx={{ height: '100%', overflow: 'hidden', borderRadius: 2 }}>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d51850.89149372326!2d-78.48638362534173!3d35.650744864886244!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89ac7ec3a0c82ee1%3A0xbc3e0cf2c5d4b363!2sClayton%2C%20NC!5e0!3m2!1sen!2sus!4v1649287051334!5m2!1sen!2sus"
                  width="100%"
                  height="350"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  title="Clayton, NC Map"
                ></iframe>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={3} sx={{ p: 4, height: '100%', borderRadius: 2, background: theme.palette.background.paper }}>
                <Typography
                  variant="h5"
                  component="h3"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    mb: 3,
                    color: theme.palette.primary.main
                  }}
                >
                  Contact Us for Clayton Lawn Care
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                  <LocationOnIcon sx={{ mr: 2, color: theme.palette.primary.main, fontSize: 28 }} />
                  <Typography variant="body1" sx={{ fontSize: '1.05rem' }}>
                    2562 US-70, Goldsboro, NC 27530
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                  <PhoneIcon sx={{ mr: 2, color: theme.palette.primary.main, fontSize: 28 }} />
                  <Typography variant="body1" sx={{ fontSize: '1.05rem' }}>
                    <a href="tel:+19192529130" style={{ color: 'inherit', textDecoration: 'none' }}>
                      (919) 252-9130
                    </a>
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                  <AccessTimeIcon sx={{ mr: 2, color: theme.palette.primary.main, fontSize: 28 }} />
                  <Typography variant="body1" sx={{ fontSize: '1.05rem' }}>
                    Mon-Fri: 8AM-6PM
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  href="/contact"
                  size="large"
                  sx={{
                    py: 1.5,
                    fontWeight: 600,
                    fontSize: '1.05rem',
                    borderRadius: 2,
                    textTransform: 'none'
                  }}
                >
                  Request a Free Quote in Clayton
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Main>
  );
};

export default ClaytonLocation; 